import { Alert, Table, TablePaginationConfig } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { useTableParams } from 'business/providers/table-params';
import { PAGE_OPTIONS } from 'business/quality-control/constants';
import { useGetListData } from 'business/quality-control/pages/defect-report-history/hooks/data/use-get-list';
import { useDefectReportHistoryColumns } from 'business/quality-control/pages/defect-report-history/hooks/use-columns';
import { FilterDataSetter } from 'technical/filter-data';

import { DefectReportDetailsDrawer } from './defect-report-details-drawer';
import { DefectReportHistoryFilterData } from '..';

export type DefectReportHistory = {
  id: string;
  segmentType: string;
  serialNumber: string | null;
  defectType: string;
  toRepair: boolean;
  indicator: string | null;
  status: string;
  reportDate: string;
  ringNumber: number;
  ringKey: number;
  defectDescription: string | null;
  defectAuthor: string;
  repairId: string | null;
  repairDate: string | null;
  repairDescription: string | null;
  repairAuthor: string | null;
};

export const DefectReportTable = ({
  setFilterData,
  mobile = false,
}: {
  setFilterData: FilterDataSetter<DefectReportHistoryFilterData>;
  mobile?: boolean;
}) => {
  const { t } = useTranslation();

  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site selected');

  const { tableParams, setTableParams, defaultTableParams } = useTableParams();

  const { data, total, isLoading, error } = useGetListData(setFilterData);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedReport, setSelectedReport] =
    useState<DefectReportHistory | null>(null);

  const handleOpenDrawer = (report: DefectReportHistory) => {
    setSelectedReport(report);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const handleAfterOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedReport(null);
    }
  };

  const columns = useDefectReportHistoryColumns(mobile, handleOpenDrawer);

  const handleChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter:
      | SorterResult<DefectReportHistory>
      | SorterResult<DefectReportHistory>[],
  ) => {
    let sortByColumn;
    if (Array.isArray(sorter)) {
      sortByColumn = sorter[0];
    } else {
      sortByColumn = sorter;
    }

    setTableParams({
      current: pagination.current ?? defaultTableParams.current,
      pageSize: pagination.pageSize ?? defaultTableParams.pageSize,
      sortOrder: sortByColumn.order ?? defaultTableParams.sortOrder,
      sortField: sortByColumn.field ?? defaultTableParams.sortField,
    });
  };

  if (error) {
    return <Alert message={t('errors.error_generic')} type="error" />;
  }

  return (
    <>
      <Table
        bordered
        scroll={{ x: 'max-content' }}
        // 90 is the value of src/business/App.scss - $header-height
        sticky={{ offsetHeader: mobile ? 0 : 90 }}
        size="small"
        dataSource={data}
        columns={columns}
        pagination={{
          current: tableParams.current,
          pageSize: tableParams.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: PAGE_OPTIONS,
        }}
        onChange={handleChange}
        loading={isLoading}
      />
      <DefectReportDetailsDrawer
        reportId={selectedReport?.id ?? null}
        reports={data}
        open={drawerOpen}
        mobile={mobile}
        setSelectedReport={setSelectedReport}
        onClose={handleCloseDrawer}
        afterOpenChange={handleAfterOpenChange}
      />
    </>
  );
};
